/**
 * report.ts — compliance-export builder
 *
 * buildReport() produceert een gestructureerd rapport-object van Firestore-data.
 * Geen UI-afhankelijkheden — puur data. Importeer in RapportView.tsx.
 */

import { ecc } from "./data";
import type { ChecklistEntry, IncidentEntry } from "./types";

// Firestore-incidents kunnen een optioneel `kind`-veld dragen
// dat bij het loggen wordt meegegeven (bijv. "instroom", "bommelding").
export interface ReportIncidentEntry extends IncidentEntry {
  kind?: string;
}

// ─── Sub-types rapport-object ────────────────────────────────────────────────

export interface ReportHeader {
  event: string;
  datum: string;
  locatie: string;
  opdrachtgever: string;
  cpTeam: string[];
  generatedAt: string; // ISO-string
}

export interface ReportSummary {
  totaal: number;
  perSeverity: Record<IncidentEntry["severity"], number>;
  open: number;
  afgehandeld: number;
  eerstemelding: number | null; // epoch ms
  laatste: number | null;       // epoch ms
}

export interface ReportInstroomRow {
  time: number;   // epoch ms
  title: string;
  percentage: number | null; // t.o.v. INSTROOM_CAP (zie constante)
}

export interface ReportMeldingRow {
  time: number;
  severity: IncidentEntry["severity"];
  title: string;
  location: string;
  by: string;
  resolved: boolean;
  body?: string;
}

export interface ReportEccRow {
  id: string;
  tijd_van: string | null;
  wat: string;
  checked: boolean;
  by: string;
  at: number | null; // epoch ms — moment van afvinken
  note: string;
}

export interface ReportBommelding {
  time: number;
  title: string;
  body?: string;
  location: string;
  by: string;
  resolved: boolean;
}

export interface ReportObject {
  header: ReportHeader;
  samenvatting: ReportSummary;
  instroom: ReportInstroomRow[];
  meldingen: ReportMeldingRow[];
  eccVoortgang: ReportEccRow[];
  bommeldingen: ReportBommelding[];
}

// ─── Constanten ──────────────────────────────────────────────────────────────

/** Vergunningsmaximum PM26 (Veiligheidsplan V4 §3). Werkelijk verkocht: n.n.b. —
 * navragen bij organisatie en hier bijwerken vóór het rapport wordt gedeeld. */
export const INSTROOM_CAP = 17_500;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Parseert het eerste getal uit een titel (ondersteunt Nederlandse
 * duizend-separator: "7.500" → 7500, "7,500" → 7500).
 */
function parseInstroomCount(title: string): number | null {
  const m = title.match(/(\d+(?:[.,]\d+)*)/);
  if (!m) return null;
  const raw = m[1];
  // Duizend-separator patroon: één punt/komma gevolgd door exact 3 cijfers
  if (/^\d+[.,]\d{3}$/.test(raw)) {
    return parseInt(raw.replace(/[.,]/, ""), 10);
  }
  const n = parseInt(raw, 10);
  return isNaN(n) ? null : n;
}

// ─── Hoofdfunctie ─────────────────────────────────────────────────────────────

export function buildReport(
  incidents: ReportIncidentEntry[],
  checklistState: ChecklistEntry[],
): ReportObject {
  const sorted = [...incidents].sort((a, b) => a.time - b.time);

  // ── Kop ──────────────────────────────────────────────────────────────────
  const header: ReportHeader = {
    event: "Pal Mundo Festival 2026",
    datum: "18 juli 2026",
    locatie: "Almeerderstrand, Almere",
    opdrachtgever: "NEXXT Productions (Kaj Uhl)",
    cpTeam: [
      "Gabriël Bouwman — CP-centralist",
    ],
    generatedAt: new Date().toISOString(),
  };

  // ── Samenvatting ─────────────────────────────────────────────────────────
  const perSeverity: Record<IncidentEntry["severity"], number> = {
    info: 0,
    "let-op": 0,
    actie: 0,
    alarm: 0,
  };
  for (const i of sorted) {
    perSeverity[i.severity] += 1;
  }
  const times = sorted.map((i) => i.time);
  // "Eerste melding" = start van de CP-activiteit: de vroegste van (a) de eerste
  // incident-melding en (b) het vroegste afgevinkte draaiboek-/checklistmoment
  // (bv. F0 "CP activeren + eerste logregel" om 09:30) — anders lijkt de CP pas
  // bij het eerste incident begonnen.
  // `at` komt uit Firestore als serverTimestamp → runtime is dat een
  // Timestamp-object (met .toMillis), ondanks het `number`-type; offline-cache
  // schrijft wél een number. Beide vormen normaliseren.
  const toMs = (v: unknown): number | null => {
    if (typeof v === "number") return v;
    if (v && typeof (v as { toMillis?: () => number }).toMillis === "function") {
      return (v as { toMillis: () => number }).toMillis();
    }
    if (v && typeof (v as { seconds?: number }).seconds === "number") {
      return (v as { seconds: number }).seconds * 1000;
    }
    return null;
  };
  const checkTimes = checklistState
    .filter((c) => c.checked)
    .map((c) => toMs(c.at))
    .filter((n): n is number => n !== null);
  const eerste = [...times, ...checkTimes].sort((a, b) => a - b)[0] ?? null;
  const samenvatting: ReportSummary = {
    totaal: sorted.length,
    perSeverity,
    open: sorted.filter((i) => !i.resolved).length,
    afgehandeld: sorted.filter((i) => i.resolved === true).length,
    eerstemelding: eerste,
    laatste: times[times.length - 1] ?? null,
  };

  // ── Instroom-reeks ───────────────────────────────────────────────────────
  const instroomRaw = sorted.filter(
    (i) =>
      i.kind === "instroom" ||
      i.title.toLowerCase().startsWith("tussenstand"),
  );
  const instroom: ReportInstroomRow[] = instroomRaw.map((i) => {
    const count = parseInstroomCount(i.title);
    return {
      time: i.time,
      title: i.title,
      percentage:
        count != null
          ? Math.round((count / INSTROOM_CAP) * 1000) / 10
          : null,
    };
  });

  // ── Volledige meldingen-tabel ────────────────────────────────────────────
  const meldingen: ReportMeldingRow[] = sorted.map((i) => ({
    time: i.time,
    severity: i.severity,
    title: i.title,
    location: i.location,
    by: i.by ?? "—",
    resolved: i.resolved ?? false,
    body: i.body,
  }));

  // ── ECC-voortgang ────────────────────────────────────────────────────────
  const checkMap = new Map(checklistState.map((c) => [c.id, c]));
  const eccVoortgang: ReportEccRow[] = ecc.map((e) => {
    const entry = checkMap.get(e.id);
    return {
      id: e.id,
      tijd_van: e.tijd_van,
      wat: e.wat,
      checked: entry?.checked ?? false,
      by: entry?.by ?? "—",
      at: entry?.at ?? null,
      note: entry?.note ?? "",
    };
  });

  // ── Bommeldingen (apart hoofdstuk indien aanwezig) ───────────────────────
  const bommeldingen: ReportBommelding[] = sorted
    .filter((i) => i.kind === "bommelding")
    .map((i) => ({
      time: i.time,
      title: i.title,
      body: i.body,
      location: i.location,
      by: i.by ?? "—",
      resolved: i.resolved ?? false,
    }));

  return { header, samenvatting, instroom, meldingen, eccVoortgang, bommeldingen };
}
